import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import qs from 'qs';
import { changeTokenState, clearAuth } from 'app/auth/slices';
import { resources } from 'constants/locale';
import Config from 'config';
import { AppConstants } from 'constants/index';
import LocalStorageService from 'services/localStorageService';
import NavigationService from 'services/navigationService';
import StoreService from 'services/storeService';
import { changeLoadingState } from 'app/settings/slices';

const { notification } = resources;

let isRefreshing = false;

interface FailedQueueItem {
  resolve: (token?: string) => void;
  reject: (error?: unknown) => void;
}
let failedQueue: FailedQueueItem[] = [];

interface NotificationLocale {
  sessionExpired?: string;
  [key: string]: unknown;
}

interface AuthData {
  refresh_token?: string;
  access_token?: string;
  [key: string]: unknown;
}

interface CurrentData {
  languageId?: keyof typeof notification;
  [key: string]: unknown;
}

const processQueue = (error: unknown, token: string | null = null): void => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token || undefined);
    }
  });
  failedQueue = [];
};

const clearAndLogout = (): void => {
  const currentData = (LocalStorageService.get('current') as CurrentData | null) || {};
  LocalStorageService.clear();
  LocalStorageService.set('current', currentData);
  processQueue({
    message: JSON.stringify({
      meta: { skipErrorHandler: true },
    }),
  }, null);

  const { pathname, search } = NavigationService.router?.state?.location || {};
  window.location.href = `${pathname || ''}${search || ''}`;
};

axios.interceptors.response.use(
  (response: AxiosResponse) => response,
  (error: AxiosError) => {
    const originalRequest = error.config as AxiosRequestConfig & { retry?: boolean };
    if (error.response?.status === 401 && !originalRequest.retry) {
      if (isRefreshing) {
        return new Promise<string | undefined>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (token && originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return axios(originalRequest);
          }).catch(() => Promise.reject({
            message: JSON.stringify({
              meta: { skipErrorHandler: true },
            }),
          }));
      }

      const auth = LocalStorageService.get('auth') as AuthData | null;
      const refToken: string | null = auth && typeof auth.refresh_token === 'string' ? auth.refresh_token : null;

      if (!refToken) {
        if (auth) {
          clearAndLogout();
        }
        return Promise.reject({
          message: JSON.stringify({
            meta: { skipErrorHandler: true },
          }),
        });
      }

      originalRequest.retry = true;
      isRefreshing = true;
      const formData = qs.stringify({
        grant_type: 'refresh_token',
        client_id: 'admin_client',
        refresh_token: refToken,
      });

      const options = {
        data: formData,
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
      };

      const { Admin, Account, Token } = AppConstants.api;
      const url = [Admin, Account, Token].join('/');
      return new Promise((resolve, reject) => {
        axios({
          method: AppConstants.methods.POST,
          baseURL: Config.baseURL,
          url,
          ...options,
        })
          .then(({ data }) => {
            LocalStorageService.set('auth', data);
            axios.defaults.headers.common.Authorization = `Bearer ${data.access_token}`;
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${data.access_token}`;
            }
            processQueue(null, data.access_token);
            StoreService.getStore().dispatch(changeTokenState(true));
            resolve(axios(originalRequest));
          })
          .catch(() => {
            const currentData = (LocalStorageService.get('current') as CurrentData | null) || {};
            const currentLanguageId = (currentData as CurrentData)?.languageId;
            const locale: NotificationLocale = (currentLanguageId && notification[currentLanguageId]) ? notification[currentLanguageId] as NotificationLocale : notification[AppConstants.defaultLanguage];
            clearAndLogout();
            // to set isAuthenticated false
            StoreService.getStore().dispatch(clearAuth());
            // if we have for ex. playerLoading state in saga and it is true,after failer we redirect to login and it does not reach to set pageLoading true
            StoreService.getStore().dispatch(changeLoadingState(false));
            reject({
              response: { data: { errorMessage: locale.sessionExpired } },
            });
          })
          .then(() => {
            isRefreshing = false;
          });
      });
    }

    return Promise.reject(error);
  },
);

export default axios;
